/**
 * Question Bank Service
 * Manages saved question templates for application and interview questions
 */

export interface QuestionTemplate {
  id: string;
  name: string;
  type: 'application' | 'interview';
  questions: any[];
  createdAt: string;
}

const STORAGE_KEY = 'passcom-question-bank';

// Seed data for initial templates
const SEED_TEMPLATES: QuestionTemplate[] = [
  {
    id: 'app-standard',
    name: 'Standard',
    type: 'application',
    questions: [
      {
        id: 1,
        type: 'Text',
        text: 'Why do you want to work for our company?',
        limit: '500',
        choices: [],
        newChoice: ''
      },
      {
        id: 2,
        type: 'Text',
        text: 'What relevant experience do you have?',
        limit: '500',
        choices: [],
        newChoice: ''
      },
      {
        id: 3,
        type: 'Multiple Choice',
        text: 'What is your preferred work schedule?',
        limit: '500',
        choices: ['Full-time', 'Part-time', 'Flexible'],
        newChoice: ''
      }
    ],
    createdAt: new Date().toISOString()
  },
  {
    id: 'int-standard',
    name: 'Standard',
    type: 'interview',
    questions: [
      {
        id: 'iq-1',
        text: 'Tell me about your previous work experience'
      },
      {
        id: 'iq-2',
        text: 'What are your strengths and weaknesses?'
      },
      {
        id: 'iq-3',
        text: 'Where do you see yourself in 5 years?'
      }
    ],
    createdAt: new Date().toISOString()
  }
];

class QuestionBankServiceClass {
  /**
   * Initialize storage with seed data if empty
   */
  private initializeStorage(): void {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_TEMPLATES));
      }
    } catch (error) {
      console.error('Failed to initialize question bank:', error);
    }
  }

  /**
   * Get all templates from storage
   */
  private getAllTemplates(): QuestionTemplate[] {
    try {
      this.initializeStorage();
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : SEED_TEMPLATES;
    } catch (error) {
      console.error('Failed to get templates:', error);
      return SEED_TEMPLATES;
    }
  }

  /**
   * Save all templates to storage
   */
  private saveAllTemplates(templates: QuestionTemplate[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    } catch (error) {
      console.error('Failed to save templates:', error);
    }
  }

  /**
   * Get templates of a specific type
   */
  getTemplates(type: 'application' | 'interview'): QuestionTemplate[] {
    const allTemplates = this.getAllTemplates();
    return allTemplates.filter(t => t.type === type);
  }

  /**
   * Save a new template or update existing one
   */
  saveTemplate(type: 'application' | 'interview', name: string, questions: any[]): QuestionTemplate {
    const allTemplates = this.getAllTemplates();
    
    // Check if template with this name and type already exists
    const existingIndex = allTemplates.findIndex(
      t => t.type === type && t.name.toLowerCase() === name.toLowerCase()
    );

    const template: QuestionTemplate = {
      id: existingIndex >= 0 
        ? allTemplates[existingIndex].id 
        : `${type}-${Date.now()}`,
      name,
      type,
      questions: JSON.parse(JSON.stringify(questions)), // Deep clone
      createdAt: existingIndex >= 0 
        ? allTemplates[existingIndex].createdAt 
        : new Date().toISOString()
    };

    if (existingIndex >= 0) {
      // Update existing
      allTemplates[existingIndex] = template;
    } else {
      // Add new
      allTemplates.push(template);
    }

    this.saveAllTemplates(allTemplates);
    return template;
  }

  /**
   * Delete a template
   */
  deleteTemplate(id: string): void {
    const allTemplates = this.getAllTemplates();
    const filtered = allTemplates.filter(t => t.id !== id);
    this.saveAllTemplates(filtered);
  }

  /**
   * Get a specific template by id
   */
  getTemplate(id: string): QuestionTemplate | null {
    const allTemplates = this.getAllTemplates();
    return allTemplates.find(t => t.id === id) || null;
  }
}

export const QuestionBankService = new QuestionBankServiceClass();

